import moment from 'moment';
import { getValueFromAppConfig } from '../../../shared/helper-utils/localStorage';
import {
  CABIN_META,
  CATEGORY_STATUS,
  CRUISE_AGE_LIMITS,
  DEFAULT_CHILD_AGE,
  DEFAULT_INFANT_AGE,
  DURATION_BUCKETS,
  FARE_TYPES,
  GUARANTEED_CABIN,
  MAX_GUESTS_PER_CABIN,
  SAILING_MONTHS_AHEAD,
  PAYMENT_CODE_AGENCY,
  PENALTY_PERCENT,
  UNSUPPORTED_GATEWAY_MODES,
} from '../constants/cruiseConstants';

export const API_DATE = 'YYYY-MM-DD';

/* ------------------------------------------------------------------ text */

const KEEP_UPPER = new Set(['ABC', 'US', 'UK', 'USA', 'II', 'III']);

const SMALL_WORDS = new Set(['and', 'of', 'the', 'to', 'at', 'in', 'on']);

/**
 * "12-DAY PANAMA CANAL: COSTA RICA & ST. KITTS" -> "Panama Canal: Costa Rica & St. Kitts".
 * Only ALL-CAPS text is re-cased; text the line already sends in mixed case
 * ("RelaxAway Half Moon Cay") is kept as it is.
 */
export const titleCase = (value) => {
  const text = String(value || '');
  if (text !== text.toUpperCase()) return text;
  return text
    .toLowerCase()
    .replace(/(^|[\s(&:,/-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase())
    .replace(/\b([A-Za-z]+)\b/g, (w, _w, offset) => {
      if (KEEP_UPPER.has(w.toUpperCase())) return w.toUpperCase();
      if (offset > 0 && SMALL_WORDS.has(w.toLowerCase()))
        return w.toLowerCase();
      return w;
    });
};

/** Strips the "12-DAY " prefix the line puts on every voyage name. */
/** The voyage name exactly as the cruise line sends it (QA: full name). */
export const cruiseTitle = (name) => String(name || '').trim();

export const plural = (n, one, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/* ----------------------------------------------------------------- money */

// Built on use: the app config (currency) is only in place after login.
const moneyFormat = () => {
  const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  try {
    return new Intl.NumberFormat('en-IN', {
      ...options,
      style: 'currency',
      currency: getValueFromAppConfig('SAAS_APP_CURRENCY') || 'INR',
    });
  } catch (e) {
    return new Intl.NumberFormat('en-IN', {
      ...options,
      style: 'currency',
      currency: 'INR',
    });
  }
};

/**
 * Amounts are treated as the app currency (INR) — no conversion — and shown
 * the Indian way with paise: ₹1,05,708.90.
 */
export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? moneyFormat().format(n) : '';
};

export const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/* ----------------------------------------------------------------- dates */

export const parseApiDate = (value) =>
  value ? moment(value, API_DATE, true) : null;

export const toApiDate = (date) => (date ? moment(date).format(API_DATE) : '');

export const formatDay = (value) => {
  const m = moment.isMoment(value) ? value : parseApiDate(value);
  return m && m.isValid() ? m.format('DD MMM YYYY') : '';
};

export const formatShortDay = (value) => {
  const m = moment.isMoment(value) ? value : parseApiDate(value);
  return m && m.isValid() ? m.format('ddd, DD MMM') : '';
};

export const formatDateRange = (from, to) =>
  [formatDay(from), formatDay(to)].filter(Boolean).join(' – ');

/** Whole years between dob and the reference date (both yyyy-MM-dd). */
export const ageOn = (dob, onDate) => {
  const birth = parseApiDate(dob);
  const ref = parseApiDate(onDate) || moment();
  if (!birth || !birth.isValid()) return null;
  return ref.diff(birth, 'years');
};

/* ---------------------------------------------------------------- guests */

export const guestCount = ({ adults = 0, children = 0, infants = 0 }) =>
  adults + children + infants;

export const guestsSummary = ({ adults = 0, children = 0, infants = 0 }) =>
  [
    plural(adults, 'Adult'),
    children ? plural(children, 'Child', 'Children') : '',
    infants ? plural(infants, 'Infant') : '',
  ]
    .filter(Boolean)
    .join(', ');

/** Keeps the ages array the same length as the count, padding with a default. */
export const resizeAges = (ages, count, fallback) =>
  Array.from({ length: count }, (_, i) =>
    Number.isFinite(ages?.[i]) ? ages[i] : fallback,
  );

/* ------------------------------------------------------- sailing month */

export const MONTH_VALUE = 'YYYY-MM';

/** "2026-12" -> "December 2026". */
export const monthLabel = (value) => {
  const m = moment(value, MONTH_VALUE, true);
  return m.isValid() ? m.format('MMMM YYYY') : '';
};

/** This month and the next SAILING_MONTHS_AHEAD - 1, for the dropdown. */
export const sailingMonthOptions = () =>
  Array.from({ length: SAILING_MONTHS_AHEAD }, (_, i) => {
    const m = moment().startOf('month').add(i, 'months');
    return { value: m.format(MONTH_VALUE), label: m.format('MMMM YYYY') };
  });

/**
 * The fromDate / toDate CRUISESEARCH expects for a sailing month: the whole
 * month, starting today when it is the current month.
 */
export const monthRange = (value) => {
  const start = moment(value, MONTH_VALUE, true);
  if (!start.isValid()) return { fromDate: '', toDate: '' };
  const today = moment().startOf('day');
  const from = moment.max(start.clone().startOf('month'), today);
  return {
    fromDate: from.format(API_DATE),
    toDate: start.clone().endOf('month').format(API_DATE),
  };
};

const isOfferedMonth = (value) =>
  sailingMonthOptions().some((o) => o.value === value);

export const defaultSearch = () => ({
  destinationCode: '',
  destinationName: '',
  month: moment().format(MONTH_VALUE),
  wheelchairAccessible: false,
  adults: 2,
  children: 0,
  infants: 0,
  childAges: [],
  infantAges: [],
});

/** Search state <-> URL query, so a results page survives a refresh. */
export const buildSearchQuery = (s) => {
  const p = new URLSearchParams();
  p.set('dest', s.destinationCode || '');
  p.set('destName', s.destinationName || '');
  p.set('month', s.month || '');
  p.set('adults', String(s.adults));
  p.set('children', String(s.children));
  p.set('infants', String(s.infants));
  if (s.childAges?.length) p.set('childAges', s.childAges.join(','));
  if (s.infantAges?.length) p.set('infantAges', s.infantAges.join(','));
  if (s.wheelchairAccessible) p.set('wheelchair', 'true');
  return p.toString();
};

const intOr = (v, fallback) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

const agesFrom = (v) =>
  v
    ? String(v)
        .split(',')
        .map((x) => parseInt(x, 10))
        .filter((x) => Number.isFinite(x))
    : [];

export const parseSearchQuery = (search) => {
  const p = new URLSearchParams(search);
  const base = defaultSearch();
  // Same occupancy rules as the guests picker, in case the URL was edited.
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const adults = clamp(intOr(p.get('adults'), 2), 1, MAX_GUESTS_PER_CABIN);
  const children = clamp(
    intOr(p.get('children'), 0),
    0,
    MAX_GUESTS_PER_CABIN - adults,
  );
  const infants = clamp(
    intOr(p.get('infants'), 0),
    0,
    Math.min(adults, MAX_GUESTS_PER_CABIN - adults - children),
  );
  return {
    destinationCode: p.get('dest') || '',
    destinationName: p.get('destName') || '',
    // A month that is no longer offered (an old link) falls back to now.
    month: isOfferedMonth(p.get('month')) ? p.get('month') : base.month,
    wheelchairAccessible: p.get('wheelchair') === 'true',
    adults,
    children,
    infants,
    childAges: resizeAges(
      agesFrom(p.get('childAges')),
      children,
      DEFAULT_CHILD_AGE,
    ),
    infantAges: resizeAges(
      agesFrom(p.get('infantAges')),
      infants,
      DEFAULT_INFANT_AGE,
    ),
  };
};

/** Body for CRUISESEARCH. */
export const toSearchRequest = (s) => ({
  destinationCode: s.destinationCode,
  destinationName: s.destinationName || '',
  ...monthRange(s.month),
  adults: s.adults,
  children: s.children,
  infants: s.infants,
  childAges: s.childAges || [],
  infantAges: s.infantAges || [],
});

/** ['adult', 'adult', 'child', ...] in the order the forms are shown. */
export const paxTypesOf = ({ adults = 0, children = 0, infants = 0 }) => [
  ...Array(adults).fill('adult'),
  ...Array(children).fill('child'),
  ...Array(infants).fill('infant'),
];

/* ------------------------------------------------------------ results */

/**
 * The list inside an API `data`: either `data` itself or the first of the
 * named properties that holds an array (CRUISECABINS answers
 * { cabins, diningOptions, ... }).
 */
export const listFrom = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  const hit = keys.map((k) => data?.[k]).find(Array.isArray);
  return hit || [];
};

export const fareTypeLabel = (fareType) =>
  FARE_TYPES[fareType]?.label || titleCase(fareType);

export const fareTypeSub = (fareType) => FARE_TYPES[fareType]?.sub || '';

export const wholePartyTotal = (fareLike) =>
  toNumber(fareLike?.wholeParty?.totalFare);

export const perAdultTotal = (fareLike) =>
  toNumber(fareLike?.perAdult?.totalFare);

/** Cheapest fare option of a cruise (what the card shows by default). */
export const cheapestFare = (cruise) => {
  const options = Array.isArray(cruise?.fareOptions) ? cruise.fareOptions : [];
  return options.reduce(
    (best, f) =>
      !best || wholePartyTotal(f) < wholePartyTotal(best) ? f : best,
    null,
  );
};

export const cruiseKey = (cruise) =>
  [cruise?.sailingId, cruise?.webItineraryCode, cruise?.startDate]
    .filter(Boolean)
    .join('|');

export const nightsOf = (cruise) => toNumber(cruise?.durationInDays);

export const portLabel = (name) =>
  String(name || '')
    .split(',')[0]
    .trim();

export const isRoundTrip = (cruise) =>
  Boolean(cruise?.startPortCode) && cruise.startPortCode === cruise.endPortCode;

export const durationBucketOf = (nights) =>
  DURATION_BUCKETS.find((b) => b.test(nights))?.key || '';

/** Facet values with counts, for a filter list. */
export const facetCounts = (list, pick) => {
  const map = new Map();
  list.forEach((item) => {
    const v = pick(item);
    if (v) map.set(v, (map.get(v) || 0) + 1);
  });
  return [...map.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => String(a.value).localeCompare(String(b.value)));
};

export const EMPTY_FILTERS = { duration: [], port: [], ship: [], line: [] };

export const applyFilters = (list, f) =>
  list.filter((c) => {
    const n = nightsOf(c);
    return (
      (!f.duration.length || f.duration.includes(durationBucketOf(n))) &&
      (!f.port.length || f.port.includes(c.startPortName)) &&
      (!f.ship.length || f.ship.includes(c.shipName)) &&
      (!f.line.length || f.line.includes(c.cruiseLineName))
    );
  });

export const sortCruises = (list, sort, fareFor) => {
  const out = [...list];
  if (sort === 'price') {
    out.sort(
      (a, b) => wholePartyTotal(fareFor(a)) - wholePartyTotal(fareFor(b)),
    );
  } else if (sort === 'priceDesc') {
    out.sort(
      (a, b) => wholePartyTotal(fareFor(b)) - wholePartyTotal(fareFor(a)),
    );
  } else if (sort === 'date') {
    out.sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  } else if (sort === 'duration') {
    out.sort((a, b) => nightsOf(a) - nightsOf(b));
  }
  return out;
};

/* ------------------------------------------------------------ itinerary */

/**
 * Stops for the timeline. A day can have several stops (canal transits), so
 * the day label shows on its first stop only.
 */
export const toItineraryRows = (stops) => {
  const list = Array.isArray(stops) ? [...stops] : [];
  list.sort((a, b) => toNumber(a.stopNumber) - toNumber(b.stopNumber));
  let lastDay = null;
  return list.map((s, i) => {
    const firstOfDay = s.dayNumber !== lastDay;
    lastDay = s.dayNumber;
    return {
      key: `${s.stopNumber}-${i}`,
      day: firstOfDay ? `Day ${toNumber(s.dayNumber) + 1}` : '',
      date: firstOfDay ? formatShortDay(s.arrivalDate) : '',
      atSea: Boolean(s.dayAtSea),
      port: s.dayAtSea ? '' : titleCase(s.portName),
      arrive: s.arrivalTime ? `Arrive ${s.arrivalTime}` : '',
      depart: s.departureTime ? `Depart ${s.departureTime}` : '',
      boarding:
        i === 0 && s.boardingTime ? `Boarding from ${s.boardingTime}` : '',
      isEnd: i === 0 || i === list.length - 1,
      isLast: i === list.length - 1,
    };
  });
};

export const itineraryStats = (stops) => {
  const list = Array.isArray(stops) ? stops : [];
  const ports = new Set(
    list
      .filter(
        (s) => !s.dayAtSea && s.portCode && !/^cruising/i.test(s.portName),
      )
      .map((s) => s.portCode),
  );
  const seaDays = new Set(
    list.filter((s) => s.dayAtSea).map((s) => s.dayNumber),
  );
  return { ports: ports.size, seaDays: seaDays.size };
};

/* ------------------------------------------------------------ categories */

export const metaLabel = (meta) => CABIN_META[meta] || meta || '';

export const categoryStatus = (status) =>
  CATEGORY_STATUS[status] || (status ? { label: status, tone: 'muted' } : null);

export const sizeLabel = (min, max) => {
  if (!min && !max) return '';
  if (!max || min === max) return `${min || max} sq ft`;
  return `${min}–${max} sq ft`;
};

export const decksLabel = (decks) =>
  (Array.isArray(decks) ? decks : []).map(titleCase).join(' · ');

/* ------------------------------------------------------------ cabins */

export const isGuaranteed = (cabin) =>
  Boolean(cabin?.cabinAssignedLater) || cabin?.cabinNumber === GUARANTEED_CABIN;

export const cabinLabel = (cabin) =>
  isGuaranteed(cabin) ? 'Guaranteed cabin' : `Cabin ${cabin?.cabinNumber}`;

export const cabinShortLabel = (cabin) =>
  isGuaranteed(cabin)
    ? 'Guaranteed (assigned later)'
    : `Cabin ${cabin?.cabinNumber}`;

export const bedLabel = (bed) => titleCase(bed?.bedName || bed?.bedCode || '');

/* ------------------------------------------------------------ payment */

/** Distinct payment modes (lower-case) of the agent's available gateways. */
export const gatewayModes = (gateways) => {
  const list = Array.isArray(gateways) ? gateways : [gateways];
  const modes = [];
  list.forEach((g) => {
    const m = String(g?.PaymentMode || '').toLowerCase();
    if (m && !modes.includes(m) && !UNSUPPORTED_GATEWAY_MODES.includes(m)) {
      modes.push(m);
    }
  });
  return modes;
};

/* ------------------------------------------------------------ dining */

export const diningLabel = (d) => titleCase(d?.diningName || d?.diningCode);

export const diningRoomLabel = (d) =>
  d?.diningRoomName
    ? `${titleCase(d.diningRoomName)} dining room`
    : 'Any time, any table';

/** Badge for a dining option's status, or null when the line sends none. */
export const diningStatus = (d) => {
  const s = String(d?.status || '').toUpperCase();
  if (!s) return null;
  if (s === 'AVAILABLE') return { label: 'Available', tone: 'success' };
  if (s === 'WAITLISTED' || s === 'WAITLIST') {
    return { label: 'Waitlisted', tone: 'danger' };
  }
  return { label: titleCase(s), tone: 'muted' };
};

/* ------------------------------------------------------------ invoice */

/** Everything the fare summary needs, read from the price-check invoice. */
export const readInvoice = (invoice) => {
  if (!invoice) return null;
  const whole = invoice.wholeParty || {};
  const schedule = Array.isArray(invoice.paymentSchedule)
    ? invoice.paymentSchedule
    : [];
  const agency = schedule.find((p) => p.paymentCode === PAYMENT_CODE_AGENCY);
  const total = toNumber(whole.totalFare);
  const agencyAmount = agency ? toNumber(agency.amount) : null;
  return {
    base: toNumber(whole.baseFare),
    tax: toNumber(whole.taxFare),
    total,
    govTax: toNumber(invoice.governmentTaxesNotInTheFare),
    ncf:
      invoice.nonCommissionableFare === undefined ||
      invoice.nonCommissionableFare === null
        ? null
        : toNumber(invoice.nonCommissionableFare),
    netPayable: agencyAmount,
    commission:
      agencyAmount !== null && agencyAmount < total
        ? total - agencyAmount
        : null,
    schedule,
    penalties: Array.isArray(invoice.cancellationPenalties)
      ? invoice.cancellationPenalties
      : [],
    promos: Array.isArray(invoice.promoCodesApplied)
      ? invoice.promoCodesApplied.filter(Boolean)
      : [],
  };
};

/**
 * Penalty rows sorted by date: each applies from its date until the next
 * one starts.
 */
export const penaltyRows = (penalties) =>
  [...(penalties || [])]
    .sort((a, b) => String(a.fromDate).localeCompare(String(b.fromDate)))
    .map((p, i) => ({
      key: `${p.fromDate}-${p.penaltyType}-${i}`,
      when: `From ${formatDay(p.fromDate)}`,
      what:
        p.penaltyType === PENALTY_PERCENT
          ? `${toNumber(p.amount)}% of total fare`
          : `${formatAmount(p.amount)} per booking`,
    }));

/* ------------------------------------------------------------ guests */

export const blankGuest = (type) => ({
  paxType: type,
  salutation: type === 'adult' ? 'MR' : 'MSTR',
  firstName: '',
  middleName: '',
  lastName: '',
  gender: 'M',
  dateOfBirth: '',
  nationality: 'Indian',
  panNumber: '',
  passportNumber: '',
  passportExpiry: '',
  passportIssuePlace: '',
  email: '',
  countryIso: 'IN',
  countryCode: '+91',
  phoneNo: '',
  address: '',
  city: '',
  state: '',
  country: 'India',
  pinCode: '',
});

export const ageLimitsOf = (type) => CRUISE_AGE_LIMITS[type];
